import { Children, useEffect, useState, useRef, useMemo, ReactNode } from 'react';
import classnames from 'classnames';
import locale from '@glu/locale';
import useStyles from './Tabs.styles';
import TabIndicator from '../TabIndicator/TabIndicator';
import TabsContext from './TabsContext';

export interface ITabsProps {
  /** attribute for accessibility purposes with the nav */
  ariaLabel?: string;
  children?: ReactNode;
  /** custom className for nav */
  className?: string;
  /**
   * Controls side of active tab indicator for vertical layout
   * (will be rendered with "right: 0" by default)
   */
  left: boolean;
  /** reference value for the given tab */
  value?: string | number;
  /** Controls tabs layout direction (horizontal by default) */
  vertical?: boolean;
  /** An optional id you can put on the wrapping nav element */
  htmlId?: string;
  /** An optional dataQa property you can put ont he wrapping nav element */
  dataQa?: string;
}

export const TabsComponent = ({
  ariaLabel,
  children,
  className,
  dataQa,
  htmlId,
  left = false,
  value = 0,
  vertical = false,
}: Partial<ITabsProps>) => {
  const wrapperRef = useRef<HTMLElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [tabValue, setTabValue] = useState(value);

  const classes = useStyles(useMemo(() => ({ left, vertical }), [left, vertical]));

  const getTabDimensions = (selectedTabIndex: number) => {
    if (!tabsRef.current || !wrapperRef.current) {
      return {
        tabLeft: 0,
        tabWidth: 0,
        tabTop: 0,
        tabHeight: 0,
        wrapperLeft: 0,
        wrapperTop: 0,
      };
    }
    const {
      height: tabHeight,
      left: tabLeft,
      top: tabTop,
      width: tabWidth,
    } = tabsRef.current.children[selectedTabIndex].getBoundingClientRect();
    const { left: wrapperLeft, top: wrapperTop } = wrapperRef.current.getBoundingClientRect();
    return {
      tabLeft,
      tabWidth,
      tabTop,
      tabHeight,
      wrapperLeft,
      wrapperTop,
    };
  };

  const getIndicatorStyle = ({
    tabHeight,
    tabLeft,
    tabTop,
    tabWidth,
    wrapperLeft,
    wrapperTop,
  }: {
    tabLeft: number;
    tabWidth: number;
    tabTop: number;
    tabHeight: number;
    wrapperLeft: number;
    wrapperTop: number;
  }) =>
    vertical
      ? {
          top: Math.round(tabTop - wrapperTop),
          height: Math.round(tabHeight),
        }
      : {
          left: Math.round(tabLeft - wrapperLeft),
          width: Math.round(tabWidth),
        };

  const updateIndicatorState = (childrenLocal: any[]) => {
    const selectedTabIndex = childrenLocal.findIndex((child) => tabValue === child.props.value);
    if (selectedTabIndex < 0) {
      return;
    }

    const style = getIndicatorStyle(getTabDimensions(selectedTabIndex));
    if (JSON.stringify(style) !== JSON.stringify(indicatorStyle)) {
      setIndicatorStyle(style);
    }
  };

  const checkAndUpdate = () => {
    const parsedChildren = Children.toArray(children);
    if (parsedChildren.length > 0) {
      updateIndicatorState(parsedChildren);
    }
  };

  useEffect(() => {
    if (window.document.fonts) {
      window.document.fonts.ready.then(checkAndUpdate);
    } else {
      checkAndUpdate();
    }
    if (value !== tabValue && value !== 0) {
      setTabValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, tabValue, Children.toArray(children).map((tab: any) => tab.props.value)]);

  return (
    <TabsContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        value: tabValue,
        setValue: setTabValue,
      }}
    >
      <div className={classes.root}>
        <nav
          ref={wrapperRef}
          aria-label={ariaLabel || locale.get('tabs')}
          className={classnames(classes.tabsWrapper, { vertical, left, right: !left }, className)}
          data-qa={dataQa}
          id={htmlId}
        >
          <div ref={tabsRef} className={classes.tabs} role="tablist">
            {children}
          </div>
          <TabIndicator
            left={left}
            onTransitionEnd={checkAndUpdate}
            style={{
              ...indicatorStyle,
            }}
            vertical={vertical}
          />
        </nav>
      </div>
    </TabsContext.Provider>
  );
};

const StyledTabs = TabsComponent;
TabsComponent.displayName = 'Tabs';

export default StyledTabs;
